import { Divider } from "@mui/material";
import { Card, Text, Spacer, useTheme, Grid } from "@geist-ui/core";
import { motion } from "framer-motion";
import React, { useState } from "react";
import { WalletPositionType } from "../../../pages/portal/dashboard/dashboard-new";
import theme from "../../../theme";
import { TARGET_INVESTMENT_TYPE } from "../../../types/fund_types";
import { Fund } from "../../../types/vc-platform/news_types";
import { FileText, ExternalLink, ChevronDown, ChevronUp } from "@geist-ui/icons";
import { PdfViewerGeistModal } from "../../../pages/direct/investment-process-v2/components/pdf-viewer-geist-modal";

type WalletPositionProps = {
  style: React.CSSProperties;
  fund?: Fund;
  position: WalletPositionType;
};

export default function WalletPosition({
  style,
  fund,
  position,
}: WalletPositionProps) {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const reportings = position.fund?.reportings;
  return (
    <div style={{ marginBottom: '10px', ...style }}>
      <Card hoverable style={{ padding: 0 }}>
        <Grid.Container
          onClick={() => setOpen(!open)}
          style={{ padding: "10px", cursor: "pointer", height: '60px' }}
          alignItems="center"
        >
          <Grid xs={8} md={6}>
            <div style={{ display: "flex", alignItems: 'center' }}>
               <img
                  src={position.fund?.tokenLogo}
                  style={{ maxWidth: "70%", maxHeight: "35px", marginRight: '10px' }}
                />
               {/* 
                // Logic for second image if needed, keeping simple for now to match V2 vibe or just fixing errors
                {position.fund?.targetInvestmentType == TARGET_INVESTMENT_TYPE.STARTUP ? (
                  <img src={position.fund.logoUrl} style={{ maxWidth: "70%", maxHeight: "35px" }} />
                ) : (
                  <img src={fund?.logo?.url} style={{ maxWidth: "70%", maxHeight: "35px" }} />
                )}
               */}
            </div>
          </Grid>
          <Grid xs={16} md={18}>
            <Text h5 style={{ textAlign: "right", margin: 0 }}>
              <b>{position.amount.toLocaleString("de-de")}€</b>
            </Text>
          </Grid>
        </Grid.Container>
        
        {open && (
         <div style={{ padding: '0 10px 10px 10px' }}>
          <Divider style={{ marginTop: "5px", marginBottom: "10px" }} />
            <Grid.Container gap={2} style={{ margin: "10px 0" }}>
              <Grid xs={12} md={6}>
                  <div>
                    <Text h5 style={{ marginBottom: "-5px" }}>
                      <b>{position.stage}</b>
                    </Text>
                    <Text p>
                      <b>Stage</b>
                    </Text>
                  </div>
              </Grid>
              <Grid xs={12} md={6}>
                  <div>
                    <Text h5 style={{ marginBottom: "-5px" }}>
                      <b>{position.fund?.fokus}</b>
                    </Text>
                    <Text p>
                      <b>Fokus</b>
                    </Text>
                  </div>
              </Grid>
            </Grid.Container>

          {reportings && reportings.length > 0 && (
            <div style={{ margin: "10px 0", width: "100%" }}>
              <Text p>
                <b>Reportings</b>
              </Text>
              <div className="flex flex-wrap gap-4">
                {reportings.map((reporting, i) => (
                  <a
                    key={i}
                    target="_blank"
                    href={reporting.document.url}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <div
                      style={{
                        minWidth: "200px",
                        padding: "10px",
                        height: "60px",
                        backgroundColor: "rgba(0,0,0,0.05)",
                        borderRadius: "5px",
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        flexDirection: "row",
                        cursor: "pointer",
                      }}
                    >
                      <img
                        className="w-auto h-full"
                        alt="pdf"
                        src="https://ik.imagekit.io/inventure/App/Icons/pdf-icon_v1n4-WvDhW.png"
                        style={{ height: "30px" }}
                      ></img>
                      <Text small className="ml-4 font-semibold">
                        {reporting.name}
                      </Text>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        )}
      </Card>
    </div>
  );
}


export function WalletPositionV2({
  style,
  fund,
  position,
}: WalletPositionProps) {
  const [open, setOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<{ url: string; name: string; fileName: string } | null>(null);
  const theme = useTheme();

  const reportings = position.fund?.reportings;
  
  return (
    <div style={{ marginBottom: '16px', width: '100%', ...style }}>
      <Card 
        width="100%"
        style={{ 
            padding: 0, 
            borderRadius: '12px', 
            border: '1px solid #eaeaea',
            overflow: 'hidden',
            background: '#fff',
            transition: 'all 0.2s ease'
        }}
      >
        <div
          onClick={() => setOpen(!open)}
          style={{ 
              cursor: 'pointer', 
              padding: '20px 24px', 
              display: 'flex', 
              alignItems: 'center',
              userSelect: 'none'
          }}
        >
          {/* Left Column: Icon + Name */}
          <div style={{ flex: '0 0 60%', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '8px', 
                background: '#fafafa', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                border: '1px solid #f0f0f0',
                flexShrink: 0
            }}>
                <FileText size={20} color="#000" />
            </div>
            <div style={{ overflow: 'hidden' }}>
                <Text p margin={0} font="15px" b style={{ color: '#000', letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{position.fund?.name}</Text>
                <Text p margin={0} font="12px" style={{ color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{position.fund?.fundName}</Text>
            </div>
          </div>

          {/* Middle Column: Amount */}
          <div style={{ flex: '1', textAlign: 'right', paddingRight: '24px' }}>
            <Text p b margin={0} font="16px" style={{ letterSpacing: '-0.02em', whiteSpace: 'nowrap', color: '#000' }}>
              {position.amount.toLocaleString("de-de")}€
            </Text>
          </div>

          {/* Right Column: Chevron */}
          <div style={{ flex: '0 0 20px', display: 'flex', justifyContent: 'flex-end', color: '#000' }}>
                {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
        </div>
        
        {open && (
          <div style={{ 
              padding: '0 24px 24px 24px', 
              background: '#fff', 
              borderTop: '1px solid #fafafa' 
          }}>
              <div style={{ height: '24px' }} />
              <Grid.Container gap={3}>
                <Grid xs={12} sm={8}>
                  <div>
                    <Text p font="11px" b style={{ color: '#000', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px', display: 'block' }}>Anlagephase</Text>
                    <Text h4 margin={0} font="16px" style={{ fontWeight: 600, color: '#000' }}>{position.stage || 'N/A'}</Text>
                  </div>
                </Grid>
                <Grid xs={12} sm={8}>
                  <div>
                    <Text p font="11px" b style={{ color: '#000', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px', display: 'block' }}>Investment-Fokus</Text>
                    <Text h4 margin={0} font="16px" style={{ fontWeight: 600, color: '#000' }}>{position.fund?.fokus || 'Venture Capital'}</Text>
                  </div>
                </Grid>
              </Grid.Container>

              {reportings && reportings.length > 0 && (
                <div style={{ marginTop: '32px' }}>
                  <Text p font="11px" b style={{ color: '#000', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px', display: 'block' }}>Verfügbare Reportings</Text>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                     {reportings.map((reporting, i) => (
                        <div
                          key={i}
                          onClick={(e) => {
                              e.stopPropagation();
                              const productName = position.fund?.name;
                              const title = productName ? `${productName} — ${reporting.name}` : reporting.name;
                              setPreviewDoc({ url: reporting.document.url, name: title, fileName: reporting.document.fileName });
                          }}
                          style={{ 
                              padding: '14px 18px',
                              borderRadius: '10px',
                              border: '1px solid #f0f0f0',
                              background: '#fff',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '14px',
                              transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = '#000';
                              e.currentTarget.style.background = '#fafafa';
                          }}
                          onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#f0f0f0';
                              e.currentTarget.style.background = '#fff';
                          }}
                        >
                           <FileText size={18} color="#000" />
                           <Text font="14px" b style={{ margin: 0, flex: 1, color: '#000' }}>
                                {reporting.name}
                           </Text>
                           <ExternalLink size={14} color="#666" />
                        </div>
                     ))}
                  </div>
                </div>
              )}
            </div>
        )}
      </Card>

      <PdfViewerGeistModal
          isOpen={previewDoc !== null}
          onClose={() => setPreviewDoc(null)}
          url={previewDoc?.url || null}
          name={previewDoc?.name || null}
          fileName={previewDoc?.fileName || null}
          watermark
      />
    </div>
  );
}
